// src/utils/workJobs.js
// Work on the house (spec docs/superpowers/specs/2026-09-25-listing-prep-design.md, section 7).
// Every rule for a job, pure: the shipped job list, the blank job, room taps, the effective status,
// the walk-through outcome (fix job + payment hold), the two linked rows (Timeline, cost tracker),
// summaries, and the checklist link. No React, no Firestore. workStore.js does the writing.
import { toYMD, diffDays, isOpenTask } from "./listingPrep";

export const WORK_STATUSES = ["planned", "scheduled", "unchecked", "checked", "skipped"];
export const PAYMENTS = ["unpaid", "held", "paid"];

const STANDARD_ROOMS = ["Living room", "Kitchen", "Hall", "Bedroom 1", "Bedroom 2", "Bedroom 3", "Bathroom"];

// Henry's shipped list (spec section 7). Settings > Task Templates can add or rename; unknown
// keys fall back to "custom".
export const DEFAULT_JOB_KINDS = [
  { key: "carpet", title: "Carpet", inHouse: false, rooms: STANDARD_ROOMS },
  { key: "paint", title: "Paint", inHouse: false, rooms: STANDARD_ROOMS },
  { key: "paint-touchups", title: "Paint touch-ups", inHouse: false, rooms: [] },
  { key: "cleaning", title: "Cleaning", inHouse: false, rooms: [], category: "Cleaner" },
  { key: "staging", title: "Staging", inHouse: false, rooms: [], category: "Stager" },
  { key: "destaging", title: "De-staging", inHouse: false, rooms: [], category: "Stager" },
  { key: "photography", title: "Photography", inHouse: false, rooms: [], category: "Photographer" },
  { key: "measuring", title: "Measuring", inHouse: false, rooms: [] },
  { key: "pre-inspection", title: "Pre-inspection", inHouse: false, rooms: [] },
  { key: "tish", title: "TISH inspection", inHouse: false, rooms: [] },
  { key: "sign-install", title: "Sign install", inHouse: false, rooms: [] },
  { key: "junk-removal", title: "Junk removal", inHouse: false, rooms: [] },
  { key: "repairs", title: "Repairs", inHouse: false, rooms: [] },
  { key: "marketing-dropoff", title: "Marketing drop-off", inHouse: true, rooms: [] },
  { key: "custom", title: "Custom", inHouse: false, rooms: [] },
];

export const kindByKey = (key, kinds = DEFAULT_JOB_KINDS) =>
  kinds.find((k) => k.key === key) || kinds.find((k) => k.key === "custom") || DEFAULT_JOB_KINDS.find((k) => k.key === "custom");

const blankRoom = (name) => ({ name, result: "", at: "", by: "" });
export const roomsFor = (key, kinds) => (kindByKey(key, kinds).rooms || []).map(blankRoom);

export const newJobId = () => `job_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;

const str = (v) => (v == null ? "" : String(v));

export const normalizeJob = (j = {}) => ({
  id: str(j.id),
  kind: str(j.kind) || "custom",
  title: str(j.title),
  scope: str(j.scope),
  rooms: Array.isArray(j.rooms) ? j.rooms.map((r) => ({ name: str(r && r.name), result: str(r && r.result), at: str(r && r.at), by: str(r && r.by) })) : [],
  vendor: j.vendor && typeof j.vendor === "object" ? j.vendor : null,
  inHouse: j.inHouse === true,
  date: toYMD(j.date),
  time: str(j.time),
  status: WORK_STATUSES.includes(j.status) ? j.status : "planned",
  ownerId: str(j.ownerId),
  cost: str(j.cost),
  paidBy: str(j.paidBy),
  payment: PAYMENTS.includes(j.payment) ? j.payment : "unpaid",
  paidAt: str(j.paidAt),
  receiptUrl: str(j.receiptUrl),
  timelineItemId: str(j.timelineItemId),
  costItemId: str(j.costItemId),
  fixOf: j.fixOf ? str(j.fixOf) : null,
  photos: Array.isArray(j.photos) ? j.photos : [],
  clientVisible: j.clientVisible !== false,
  taskId: str(j.taskId),
  // The deal's own date field this job's date is kept on (Photography, Staging, De-staging), set by
  // workStore when the job takes the listing's Timeline row of that kind. "" for every other job.
  linkedField: str(j.linkedField),
  notes: str(j.notes),
  createdAt: str(j.createdAt),
  createdBy: str(j.createdBy),
  checkedAt: str(j.checkedAt),
  checkedBy: str(j.checkedBy),
  reminded: j.reminded && typeof j.reminded === "object" ? { ...j.reminded } : {},
  escalatedToAgentAt: str(j.escalatedToAgentAt),
  escalatedToOwnerAt: str(j.escalatedToOwnerAt),
});

export const blankJob = (key, { ownerId = "", kinds } = {}) => {
  const kind = kindByKey(key, kinds);
  return normalizeJob({ id: newJobId(), kind: kind.key, title: kind.key === "custom" ? "" : kind.title, inHouse: kind.inHouse === true, rooms: roomsFor(kind.key, kinds), ownerId });
};

// blank -> pass -> fail -> blank.
export const tapRoom = (job, name, { by = "", today = "" } = {}) => ({
  ...job,
  rooms: (job.rooms || []).map((r) => {
    if (r.name !== name) return r;
    if (r.result === "") return { name, result: "pass", at: today, by };
    if (r.result === "pass") return { name, result: "fail", at: today, by };
    return blankRoom(name);
  }),
});

// What the row shows and the ladder reads: a dated job that nobody checked is "unchecked" the day
// after its date. Stored status stays "scheduled" until the cron or a walk-through writes it.
export const effectiveStatus = (job, today) => {
  const s = job && job.status;
  if (s === "checked" || s === "skipped" || s === "unchecked") return s;
  const date = toYMD(job && job.date);
  if (!date) return "planned";
  return diffDays(toYMD(today), date) > 0 ? "unchecked" : "scheduled";
};

// Days since the scheduled date for an open dated job; null when not applicable.
export const jobDaysLate = (job, today) => {
  const s = effectiveStatus(job, today);
  if (s !== "scheduled" && s !== "unchecked") return null;
  const date = toYMD(job && job.date);
  if (!date) return null;
  const n = diffDays(toYMD(today), date);
  return Number.isNaN(n) || n < 0 ? null : n;
};

const roomsWord = (n) => `${n} room${n === 1 ? "" : "s"}`;

// Finish walk-through. Every room needs a result. Any fail keeps the parent unchecked, holds the
// payment (unless already paid), and creates ONE fix job listing the failed rooms. The fix job
// starts hidden from the seller; someone turns "Seller can see this" on once it is booked.
export const walkThroughOutcome = (job, { by = "", today = "" } = {}) => {
  const rooms = job.rooms || [];
  const blank = rooms.filter((r) => !r.result).length;
  if (blank) return { ok: false, reason: `${roomsWord(blank)} still needs a pass or a fail.` };
  const failed = rooms.filter((r) => r.result === "fail");
  if (!failed.length) {
    return { ok: true, job: { ...job, status: "checked", checkedAt: today, checkedBy: by, payment: job.payment === "held" ? "unpaid" : job.payment }, fixJob: null };
  }
  const fixJob = normalizeJob({
    id: newJobId(), kind: job.kind, title: `Fix: ${job.title || kindByKey(job.kind).title}, ${failed.map((r) => r.name).join(", ")}`,
    scope: "", rooms: failed.map((r) => blankRoom(r.name)), vendor: job.vendor, inHouse: job.inHouse, ownerId: job.ownerId,
    status: "planned", payment: "unpaid", cost: "", paidBy: "", fixOf: job.id, clientVisible: false, createdAt: today, createdBy: by,
  });
  return { ok: true, job: { ...job, status: "unchecked", payment: job.payment === "paid" ? "paid" : "held" }, fixJob };
};

// The fix job passed: its parent is checked and the hold is released.
export const parentAfterFixChecked = (parent, today, by) => ({
  ...parent, status: "checked", checkedAt: today, checkedBy: by, payment: parent.payment === "held" ? "unpaid" : parent.payment,
  rooms: (parent.rooms || []).map((r) => (r.result === "fail" ? { ...r, result: "pass", at: today, by } : r)),
});

const TIMELINE_STATUS = { planned: "planned", scheduled: "scheduled", unchecked: "scheduled", checked: "done", skipped: "skipped" };

// The Timeline row this job owns. Same fields TimelineItemDialog saves (utils/timelineItemForm.js
// blankItem); the catalog key is reused when the job kind is one the Timeline knows, else "custom".
const TIMELINE_KEYS = new Set(["carpet", "paint", "cleaning", "measuring", "photos", "sign", "tish", "destaging"]);
export const timelineKeyFor = (kind) =>
  kind === "photography" ? "photos" : kind === "staging" ? "stagingInstall" : kind === "sign-install" ? "sign" : kind === "pre-inspection" ? "preInspection" : TIMELINE_KEYS.has(kind) ? kind : "custom";

// The three jobs whose date IS one of the listing's own dates (the same fields the Timeline's
// catalog rows and the checklist's count-back rules read). A fix job never is: its date is the
// redo, not the listing's photo or staging day.
const LISTING_DATE_FIELDS = { photography: "photographerDate", staging: "stagingInstall", destaging: "destaging" };
export const listingDateFieldFor = (job) => (job && !job.fixOf ? LISTING_DATE_FIELDS[str(job.kind)] || "" : "");

// A job with a linkedField stores NO date on its row, like every linked Timeline item: readers
// resolve deal[linkedField], which workStore writes in the same save.
export const jobToTimelineItem = (job) => ({
  id: str(job.timelineItemId),
  key: timelineKeyFor(job.kind),
  title: job.title || kindByKey(job.kind).title,
  side: "seller",
  linkedField: str(job.linkedField),
  date: job.linkedField ? "" : toYMD(job.date),
  endDate: "",
  time: str(job.time),
  status:
    TIMELINE_STATUS[
      job.date
        ? job.status === "planned"
          ? "scheduled"
          : job.status
        : job.status === "checked" || job.status === "skipped"
          ? job.status
          : "planned"
    ] || "planned",
  clientVisible: job.clientVisible !== false,
  clientNote: str(job.scope),
  notes: "",
  vendor: job.vendor || null,
  workJobId: str(job.id),
});

// The cost tracker row (utils/costReceiptTracker.js blankItem shape). Receipt fields and
// "reimbursed" belong to the tracker; they are kept from the existing row.
export const jobToCostItem = (job, existing) => ({
  id: str(job.costItemId),
  description: job.title || kindByKey(job.kind).title,
  vendor: job.inHouse ? "In house" : job.vendor ? (job.vendor.company || job.vendor.name || "") : "",
  done: job.status === "checked",
  cost: str(job.cost),
  paidBy: str(job.paidBy),
  reimbursed: !!(existing && existing.reimbursed),
  receiptUrl: str(existing && existing.receiptUrl) || str(job.receiptUrl),
  receiptName: str(existing && existing.receiptName),
  receiptFileId: str(existing && existing.receiptFileId),
  // Paid in Full (Henry 10/14/2026) belongs to the tracker and the Stager Portal, like reimbursed: kept.
  ...(existing && existing.paidInFull === true
    ? { paidInFull: true, paidInFullAt: str(existing.paidInFullAt), paidInFullBy: str(existing.paidInFullBy) }
    : {}),
  workJobId: str(job.id),
});

// The fields of an EXISTING Timeline row a job save rewrites. The rest of the row belongs to the
// Timeline tab (internal notes, end date, order) and is left as typed there; a listing's own
// catalog row (a linked one) also keeps its title.
export const jobToTimelinePatch = (job) => {
  const { notes, endDate, title, ...rest } = jobToTimelineItem(job);
  return job.linkedField ? rest : { ...rest, title };
};

const TIMELINE_TO_JOB = { planned: "planned", scheduled: "scheduled", done: "checked", skipped: "skipped" };
const roomsOpen = (job) => (job.rooms || []).some((r) => r.result !== "pass");

// An edit made on the Timeline tab to a job's row, carried back to the job so the next save on
// the Work tab cannot put the old values back. `item` is the row as just saved, `date` its date
// (a linked row's is the deal's). Done on a job whose rooms are not all passed is refused
// (blocked: "rooms"): the walk-through on the Work tab is what checks that job. Returns
// { job, blocked, checked, skipped }.
export const jobAfterTimelineEdit = (job, item, { date = "", today = "", by = "" } = {}) => {
  const next = {
    ...job,
    date: toYMD(date),
    time: str(item.time),
    vendor: item.vendor || null,
    clientVisible: item.clientVisible !== false,
    scope: str(item.clientNote),
    title: job.linkedField ? job.title : str(item.title) || job.title,
  };
  const want = TIMELINE_TO_JOB[item.status] || "";
  const wasClosed = job.status === "checked" || job.status === "skipped";
  let blocked = "";
  if (want === "checked" && job.status !== "checked") {
    if (roomsOpen(job)) blocked = "rooms";
    else Object.assign(next, { status: "checked", checkedAt: today, checkedBy: by, payment: job.payment === "held" ? "unpaid" : job.payment });
  } else if (want === "skipped" && job.status !== "skipped") {
    Object.assign(next, { status: "skipped", notes: job.notes || "Skipped on the Timeline" });
  } else if ((want === "planned" || want === "scheduled") && wasClosed) {
    Object.assign(next, { status: next.date ? "scheduled" : "planned", checkedAt: "", checkedBy: "" });
  }
  return { job: normalizeJob(next), blocked, checked: next.status === "checked" && job.status !== "checked", skipped: next.status === "skipped" && job.status !== "skipped" };
};

// The status a job's Timeline row shows (what jobToTimelineItem writes).
export const timelineStatusFor = (job) => jobToTimelineItem(job).status;

// A hand-off on the checklist moves the linked Work job with it, so the job's reminders follow the
// task's new owner instead of chasing the old one. before/after are a deal's task lists around one
// save; returns [{ jobId, ownerId }] for linked tasks whose owner changed.
export const jobOwnerChanges = (before, after) => {
  const was = new Map();
  (Array.isArray(before) ? before : []).forEach((t) => { if (t && t.workJobId) was.set(str(t.workJobId), str(t.assignee && t.assignee.id)); });
  const out = [];
  (Array.isArray(after) ? after : []).forEach((t) => {
    if (!t || !t.workJobId || !was.has(str(t.workJobId))) return;
    const ownerId = str(t.assignee && t.assignee.id);
    if (ownerId && ownerId !== was.get(str(t.workJobId))) out.push({ jobId: str(t.workJobId), ownerId });
  });
  return out;
};

export const paidBeforeChecked = (job) => {
  if (!job || job.payment !== "paid") return false;
  if (job.status !== "checked") return true;
  if (!job.paidAt) return false;
  // paid and checked, but no checkedAt to prove the order: flag it.
  if (!job.checkedAt) return true;
  return job.paidAt < job.checkedAt;
};

export const jobsSummary = (jobs, today) => {
  const list = (Array.isArray(jobs) ? jobs : []).filter((j) => j && j.status !== "skipped");
  const statuses = list.map((j) => effectiveStatus(j, today));
  return {
    total: list.length,
    open: statuses.filter((s) => s !== "checked").length,
    unchecked: statuses.filter((s) => s === "unchecked").length,
    held: list.filter((j) => j.payment === "held").length,
    paidBeforeChecked: list.filter(paidBeforeChecked).length,
  };
};

export const jobIdForTask = (taskId) => `job_task_${str(taskId)}`;

// Checklist rows flagged with a workKind become in-house jobs the first time the Work tab opens on
// the listing. Deterministic ids, so opening the tab twice never doubles them. The task's deadline
// is an internal due date, not an appointment: the job starts undated and hidden from the seller,
// so nothing reaches the portal or the day-before client email until someone books a real date.
export const jobsFromChecklist = (tasks, existingJobs, kinds) => {
  const have = new Set((existingJobs || []).map((j) => str(j.id)));
  return (Array.isArray(tasks) ? tasks : [])
    // An item marked N/A (10/06/2026) gets no job, like a Completed one.
    .filter((t) => t && t.workKind && isOpenTask(t) && !t.workJobId && !have.has(jobIdForTask(t.id)))
    .map((t) => {
      const kind = kindByKey(t.workKind, kinds);
      return normalizeJob({
        id: jobIdForTask(t.id), kind: kind.key, title: t.title || kind.title, inHouse: kind.inHouse === true, rooms: roomsFor(kind.key, kinds),
        date: "", status: "planned", clientVisible: false, taskId: str(t.id), ownerId: str(t.assignee && t.assignee.id),
      });
    });
};
